import { LLMResponse } from '../types';

function isK1Grade(grade?: string): boolean {
  const normalized = String(grade || '').toLowerCase();
  return normalized.includes('k') || normalized.includes('1st');
}

function capitalizeAt(text: string, index: number): string {
  return text.slice(0, index) + text.charAt(index).toUpperCase() + text.slice(index + 1);
}

function indexOfFirstLetter(text: string, from: number = 0): number {
  for (let i = from; i < text.length; i += 1) {
    if (/[A-Za-z]/.test(text.charAt(i))) {
      return i;
    }
  }
  return -1;
}

function capitalizeSentenceStarts(text: string): string {
  const firstLetterIndex = indexOfFirstLetter(text);
  if (firstLetterIndex !== -1) {
    text = capitalizeAt(text, firstLetterIndex);
  }

  return text.replace(/([.!?])(\s+)(["']*)([a-z])/g, (_match, punct, space, quotes, letter) => {
    return `${punct}${space}${quotes}${letter.toUpperCase()}`;
  });
}

function ensureEndingPunctuation(text: string): string {
  const match = text.match(/^(.*?)(["']*)$/);
  const body = match?.[1] ?? text;
  const trailingQuotes = match?.[2] ?? '';

  if (/[.!?]$/.test(body)) {
    return text;
  }

  if (/[,;:]$/.test(body)) {
    return `${body.slice(0, -1)}.${trailingQuotes}`;
  }

  return `${body}.${trailingQuotes}`;
}

/**
 * Answer-key rules: start with a capital letter and end with . ! or ?
 * Also capitalizes the start of later sentences in a short paragraph.
 */
export function formatCorrectSentence(sentence: string): string {
  if (!sentence || typeof sentence !== 'string') {
    return sentence;
  }

  const trimmed = sentence.trim();
  if (!trimmed) {
    return sentence;
  }

  return ensureEndingPunctuation(capitalizeSentenceStarts(trimmed));
}

export function finalizeSentencePair(
  incorrectSentence: string,
  correctSentence: string,
  grade?: string
): { incorrectSentence: string; correctSentence: string } {
  const formattedCorrect = formatCorrectSentence(correctSentence);

  // K-1 is spelling-only, so apply the same start/end rules to the prompt
  // sentence. That way fixing the answer key does not invent cap/punct work.
  if (isK1Grade(grade)) {
    return {
      incorrectSentence: formatCorrectSentence(incorrectSentence),
      correctSentence: formattedCorrect
    };
  }

  return {
    incorrectSentence,
    correctSentence: formattedCorrect
  };
}

export function finalizeLLMResponse(response: LLMResponse, grade?: string): LLMResponse {
  return {
    ...response,
    ...finalizeSentencePair(response.incorrectSentence, response.correctSentence, grade)
  };
}

export function finalizeGameSentence<T extends { incorrectSentence: string; correctSentence: string }>(
  sentence: T,
  grade?: string
): T {
  return {
    ...sentence,
    ...finalizeSentencePair(sentence.incorrectSentence, sentence.correctSentence, grade)
  };
}
